import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { MapPin, Plus, LogOut, Users, ClipboardList, MessageSquare, Camera, Trash2, Download, RotateCcw, ExternalLink, Settings } from 'lucide-react';
import { toast } from 'sonner';
import ProfileSettings from '@/components/ProfileSettings';
import ChatThread from '@/components/ChatThread';
import StartChatDialog from '@/components/StartChatDialog';
import MISReportDownload from '@/components/MISReportDownload';
import TaskFilters from '@/components/TaskFilters';
import DeadlineCountdown from '@/components/DeadlineCountdown';
import EditTaskDialog from '@/components/EditTaskDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [workLogs, setWorkLogs] = useState([]);
  const [messages, setMessages] = useState([]);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [isFiltering, setIsFiltering] = useState(false);
  const [activeTab, setActiveTab] = useState('tasks');

  useEffect(() => {
    fetchData();
    fetchUnreadCount();
    
    // Poll only critical data every 30 seconds (lighter load)
    const interval = setInterval(() => {
      fetchTasks(); // Only fetch tasks
      fetchUnreadCount();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchTasks = async () => {
    try {
      const config = {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('session_token') || ''}`
        }
      };
      const response = await axios.get(`${API}/tasks`, config);
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const fetchData = async () => {
    try {
      const config = {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('session_token') || ''}`
        }
      };
      const [tasksRes, usersRes, logsRes, messagesRes] = await Promise.all([
        axios.get(`${API}/tasks`, config),
        axios.get(`${API}/users`, config),
        axios.get(`${API}/work-logs`, config),
        axios.get(`${API}/messages`, config)
      ]);
      setTasks(tasksRes.data);
      setUsers(usersRes.data);
      setWorkLogs(logsRes.data);
      setMessages(messagesRes.data);
      fetchUnreadCount(); // Refresh unread count when data is fetched
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const config = {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('session_token') || ''}`
        }
      };
      const response = await axios.get(`${API}/messages/unread-count`, config);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const createTask = async (formData) => {
    try {
      const config = {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('session_token') || ''}`
        }
      };
      await axios.post(`${API}/tasks`, formData, config);
      toast.success('Task created successfully');
      setShowCreateTask(false);
      fetchData();
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error('Failed to create task');
    }
  };

  const updateUserRole = async (userId, role) => {
    try {
      const config = {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('session_token') || ''}`
        }
      };
      await axios.patch(`${API}/users/${userId}/role?role=${role}`, {}, config);
      toast.success('User role updated');
      fetchData();
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Failed to update role');
    }
  };

  const deleteUser = async (userId) => {
    try {
      const config = {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('session_token') || ''}`
        }
      };
      await axios.delete(`${API}/users/${userId}`, config);
      toast.success('User deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting user:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to delete user';
      toast.error(errorMsg);
    }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm('Delete this task and all related data?')) return;
    
    try {
      const config = {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('session_token') || ''}`
        }
      };
      await axios.delete(`${API}/tasks/${taskId}`, config);
      toast.success('Task deleted successfully');
      fetchData();
      setSelectedTask(null);
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };

  const handleStartChat = (task) => {
    setSelectedChat(task);
    setActiveTab('messages'); // Switch to messages tab
    toast.success(`Chat opened with ${task.assigned_to_name}`);
  };

  const handleFilter = async (filters) => {
    try {
      setIsFiltering(true);
      const config = {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('session_token') || ''}`
        }
      };
      
      const params = new URLSearchParams();
      if (filters.query) params.append('query', filters.query);
      if (filters.status) params.append('status', filters.status);
      if (filters.assigned) params.append('assigned', filters.assigned);
      if (filters.location) params.append('location', filters.location);
      
      const response = await axios.get(`${API}/tasks/search?${params.toString()}`, config);
      setFilteredTasks(response.data);
    } catch (error) {
      console.error('Error filtering tasks:', error);
      toast.error('Failed to filter tasks');
    } finally {
      setIsFiltering(false);
    }
  };

  const handleClearFilters = () => {
    setFilteredTasks([]);
    setIsFiltering(false);
  };

  const deleteWorkLog = async (logId) => {
    if (!window.confirm('Delete this work log?')) return;
    
    try {
      const config = {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('session_token') || ''}`
        }
      };
      await axios.delete(`${API}/work-logs/${logId}`, config);
      toast.success('Work log deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting work log:', error);
      toast.error('Failed to delete work log');
    }
  };

  const reopenTask = async (taskId, newStatus) => {
    try {
      const config = {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('session_token') || ''}`
        }
      };
      await axios.patch(`${API}/tasks/${taskId}`, { status: newStatus }, config);
      toast.success(`Task reopened as ${newStatus.replace('_', ' ')}`);
      fetchData();
    } catch (error) {
      console.error('Error reopening task:', error);
      toast.error('Failed to reopen task');
    }
  };

  const downloadPhoto = (photoUrl, index) => {
    const link = document.createElement('a');
    link.href = photoUrl;
    link.download = `photo_${index + 1}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Photo downloaded');
  };

  const downloadAllPhotos = (photos, taskTitle) => {
    photos.forEach((photo, index) => {
      setTimeout(() => {
        const link = document.createElement('a');
        link.href = photo;
        link.download = `${taskTitle.replace(/\s+/g, '_')}_photo_${index + 1}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }, index * 100);
    });
    toast.success(`Downloading ${photos.length} photos`);
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      in_progress: 'bg-blue-100 text-blue-800 border-blue-300',
      completed: 'bg-green-100 text-green-800 border-green-300'
    };
    return variants[status] || variants.pending;
  };

  const fieldStaff = users.filter(u => u.role === 'field_staff');
  const stats = {
    totalTasks: tasks.length,
    pendingTasks: tasks.filter(t => t.status === 'pending').length,
    inProgressTasks: tasks.filter(t => t.status === 'in_progress').length,
    completedTasks: tasks.filter(t => t.status === 'completed').length,
    totalStaff: fieldStaff.length,
    totalWorkLogs: workLogs.length
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center">
              <MapPin className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">FieldMate Admin</h1>
              <p className="text-sm text-slate-500">Welcome, {user?.name}</p>
            </div>
            <span className="ml-4 bg-blue-600 text-white px-3 py-1 rounded text-sm font-bold">
              Unread: {unreadCount}
            </span>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={() => setShowProfileSettings(true)}
              variant="outline"
              data-testid="profile-settings-btn"
              className="gap-2"
            >
              <Settings className="w-4 h-4" />
              Profile
            </Button>
            <Button 
              onClick={logout} 
              variant="outline"
              data-testid="logout-btn"
              className="gap-2"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <StatsCard title="Total Tasks" value={stats.totalTasks} icon={<ClipboardList />} color="teal" />
          <StatsCard title="Pending" value={stats.pendingTasks} icon={<ClipboardList />} color="yellow" />
          <StatsCard title="In Progress" value={stats.inProgressTasks} icon={<ClipboardList />} color="blue" />
          <StatsCard title="Completed" value={stats.completedTasks} icon={<ClipboardList />} color="green" />
          <StatsCard title="Field Staff" value={stats.totalStaff} icon={<Users />} color="purple" />
          <StatsCard title="Work Logs" value={stats.totalWorkLogs} icon={<Camera />} color="orange" />
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="tasks" data-testid="tasks-tab">Tasks</TabsTrigger>
            <TabsTrigger value="staff" data-testid="staff-tab">Staff</TabsTrigger>
            <TabsTrigger value="logs" data-testid="logs-tab">Work Logs</TabsTrigger>
            <TabsTrigger value="messages" data-testid="messages-tab">
              Messages
              <span className={`ml-2 inline-flex items-center justify-center ${unreadCount > 0 ? 'bg-red-600' : 'bg-gray-400'} text-white rounded-full px-2 py-1 text-xs font-bold min-w-[20px]`}>
                {unreadCount}
              </span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="tasks">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-slate-900">Tasks</h2>
              <div className="flex gap-2">
                <MISReportDownload />
                <Dialog open={showCreateTask} onOpenChange={setShowCreateTask}>
                  <DialogTrigger asChild>
                    <Button className="bg-teal-600 hover:bg-teal-700 gap-2" data-testid="create-task-btn">
                      <Plus className="w-4 h-4" />
                      Create Task
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="create-task-dialog">
                    <CreateTaskForm users={fieldStaff} onSubmit={createTask} onCancel={() => setShowCreateTask(false)} />
                  </DialogContent>
                </Dialog>
              </div>
            </div>
            
            {/* Task Filters */}
            <TaskFilters 
              users={users} 
              onFilter={handleFilter} 
              onClear={handleClearFilters}
            />
            
            <div className="grid gap-4">
              {(filteredTasks.length > 0 || isFiltering ? filteredTasks : tasks).map(task => (
                <TaskCard 
                  key={task.id} 
                  task={task} 
                  onSelect={() => setSelectedTask(task)}
                  onDelete={deleteTask}
                  onReopen={reopenTask}
                  onStartChat={handleStartChat}
                  users={users}
                  onEdit={fetchData}
                />
              ))}
              {isFiltering && filteredTasks.length === 0 && (
                <Card className="p-8 text-center">
                  <p className="text-slate-500">No tasks found matching your filters</p>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="staff">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Field Staff</h2>
            <div className="grid gap-4">
              {users.map(u => (
                <StaffCard 
                  key={u.id} 
                  user={u} 
                  onRoleUpdate={updateUserRole} 
                  onDelete={deleteUser}
                  currentUserId={user?.id}
                  tasks={tasks}
                  onStartChat={handleStartChat}
                />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="logs">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Work Logs</h2>
            <div className="grid gap-4">
              {workLogs.map(log => (
                <WorkLogCard 
                  key={log.id} 
                  log={log} 
                  tasks={tasks}
                  onDelete={deleteWorkLog}
                  onDownloadPhoto={downloadPhoto}
                  onDownloadAll={downloadAllPhotos}
                />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="messages">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Messages & Conversations</h2>
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Task List with Message Count */}
              <div>
                <h3 className="font-semibold text-slate-700 mb-3">Active Conversations</h3>
                <div className="space-y-2">
                  {tasks.filter(t => {
                    const taskMessages = messages.filter(m => m.task_id === t.id);
                    return taskMessages.length > 0;
                  }).map(task => {
                    const taskMessages = messages.filter(m => m.task_id === task.id);
                    const unreadMessages = taskMessages.filter(m => m.to_user_id === user.id && m.read_status === false);
                    const lastMessage = taskMessages[taskMessages.length - 1];
                    return (
                      <Card 
                        key={task.id}
                        className={`p-4 cursor-pointer hover:shadow-md transition-shadow ${
                          selectedChat?.id === task.id ? 'border-teal-500 border-2' : ''
                        }`}
                        onClick={() => setSelectedChat(task)}
                        data-testid={`chat-task-${task.id}`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-bold text-slate-900">{task.title}</h4>
                          <div className="flex gap-2">
                            <Badge className="bg-teal-100 text-teal-800">
                              {taskMessages.length} total
                            </Badge>
                            {unreadMessages.length > 0 && (
                              <Badge className="bg-red-600 text-white">
                                {unreadMessages.length} unread
                              </Badge>
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-slate-600">
                          With: {task.assigned_to_name}
                        </p>
                        {lastMessage && (
                          <p className="text-xs text-slate-400 mt-2">
                            Last: {new Date(lastMessage.timestamp).toLocaleString()}
                          </p>
                        )}
                      </Card>
                    );
                  })}
                  {tasks.filter(t => messages.filter(m => m.task_id === t.id).length > 0).length === 0 && (
                    <p className="text-slate-500 text-center py-8">No conversations yet</p>
                  )}
                </div>
              </div>

              {/* Chat Thread */}
              <div>
                {selectedChat ? (
                  <Card className="h-[600px] flex flex-col">
                    <ChatThread 
                      task={selectedChat}
                      currentUser={user}
                      onMessageSent={() => {
                        fetchData();
                        fetchUnreadCount();
                      }}
                    />
                  </Card>
                ) : (
                  <Card className="h-[600px] flex items-center justify-center">
                    <div className="text-center text-slate-500">
                      <MessageSquare className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                      <p>Select a conversation to start chatting</p>
                    </div>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Task Detail Dialog */}
      {selectedTask && (
        <Dialog open={!!selectedTask} onOpenChange={() => setSelectedTask(null)}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <TaskDetail 
              task={selectedTask} 
              workLogs={workLogs.filter(l => l.task_id === selectedTask.id)}
              onDownloadPhoto={downloadPhoto}
              onDownloadAll={downloadAllPhotos}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Profile Settings Dialog */}
      {showProfileSettings && (
        <ProfileSettings 
          user={user}
          onClose={() => setShowProfileSettings(false)}
          onUpdate={(updatedUser) => {
            setUser(updatedUser);
            checkAuth();
          }}
        />
      )}
    </div>
  );
};

const StatsCard = ({ title, value, icon, color }) => {
  const colors = {
    teal: 'bg-teal-100 text-teal-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600'
  };
  return (
    <Card className="p-4">
      <div className={`w-10 h-10 ${colors[color]} rounded-lg flex items-center justify-center mb-2`}>
        {React.cloneElement(icon, { className: 'w-5 h-5' })}
      </div>
      <p className="text-sm text-slate-600">{title}</p>
      <p className="text-2xl font-bold text-slate-900">{value}</p>
    </Card>
  );
};

const TaskCard = ({ task, onSelect, onDelete, onReopen, onStartChat, users, onEdit }) => {
  const getStatusBadge = (status) => {
    const variants = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      in_progress: 'bg-blue-100 text-blue-800 border-blue-300',
      completed: 'bg-green-100 text-green-800 border-green-300',
      overdue: 'bg-red-100 text-red-800 border-red-300'
    };
    return variants[status] || variants.pending;
  };

  return (
    <Card className="p-6 hover:shadow-md transition-shadow" data-testid={`task-card-${task.id}`}>
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 cursor-pointer" onClick={onSelect}>
          <h3 className="text-lg font-bold text-slate-900 mb-1">{task.title}</h3>
          <p className="text-sm text-slate-600">{task.description}</p>
        </div>
        <div className="flex gap-2 items-start">
          <Badge className={`${getStatusBadge(task.status)} border`}>
            {task.status.replace('_', ' ')}
          </Badge>
        </div>
      </div>
      <div className="flex justify-between items-center gap-4 text-sm text-slate-500 mb-3">
        <div className="flex gap-4">
          <span>Assigned to: <strong>{task.assigned_to_name || 'Unknown'}</strong></span>
          {task.customer_name && <span>Customer: <strong>{task.customer_name}</strong></span>}
        </div>
        {task.deadline && (
          <DeadlineCountdown deadline={task.deadline} status={task.status} />
        )}
      </div>
      {task.extension_requested && (
        <div className="mb-3">
          <Badge className="bg-orange-100 text-orange-800">
            Extension Requested: {task.extension_reason}
          </Badge>
        </div>
      )}
      <div className="flex gap-2">
        {/* Edit Button */}
        <div onClick={(e) => e.stopPropagation()}>
          <EditTaskDialog task={task} users={users} onSuccess={onEdit} />
        </div>
        
        {/* Start Chat Button - only show for assigned tasks */}
        {task.assigned_to && task.assigned_to_name !== 'Unassigned' && (
          <Button
            onClick={(e) => { e.stopPropagation(); onStartChat(task); }}
            variant="outline"
            size="sm"
            className="gap-1 text-teal-600 hover:text-teal-700 hover:bg-teal-50"
            data-testid={`start-chat-btn-${task.id}`}
          >
            <MessageSquare className="w-3 h-3" />
            Chat
          </Button>
        )}
        {task.status === 'completed' && (
          <Button
            onClick={(e) => { e.stopPropagation(); onReopen(task.id, 'in_progress'); }}
            variant="outline"
            size="sm"
            className="gap-1"
            data-testid={`reopen-task-btn-${task.id}`}
          >
            <RotateCcw className="w-3 h-3" />
            Reopen
          </Button>
        )}
        <Button
          onClick={(e) => { e.stopPropagation(); onDelete(task.id); }}
          variant="outline"
          size="sm"
          className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
          data-testid={`delete-task-btn-${task.id}`}
        >
          <Trash2 className="w-3 h-3" />
          Delete
        </Button>
      </div>
    </Card>
  );
};

const StaffCard = ({ user, onRoleUpdate, onDelete, currentUserId, tasks, onStartChat }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  return (
    <Card className="p-6" data-testid={`staff-card-${user.id}`}>
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <img src={user.picture || 'https://via.placeholder.com/50'} alt={user.name} className="w-12 h-12 rounded-full" />
          <div>
            <h3 className="font-bold text-slate-900">{user.name}</h3>
            <p className="text-sm text-slate-600">{user.email}</p>
            <Badge className={user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}>
              {user.role === 'admin' ? 'Admin' : 'Field Staff'}
            </Badge>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Start Chat Button for Field Staff */}
          {user.role === 'field_staff' && (
            <StartChatDialog user={user} tasks={tasks} onStartChat={onStartChat} />
          )}
          <Select value={user.role} onValueChange={(role) => onRoleUpdate(user.id, role)}>
            <SelectTrigger className="w-40" data-testid={`role-select-${user.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="field_staff">Field Staff</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
          
          {user.id !== currentUserId && (
            <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
              <DialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid={`delete-user-btn-${user.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Delete User</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-slate-600">
                    Are you sure you want to delete <strong>{user.name}</strong>?
                  </p>
                  <p className="text-sm text-slate-500">
                    • Tasks assigned to this user will be marked as "Unassigned"<br />
                    • Work logs and messages will be preserved<br />
                    • This action cannot be undone
                  </p>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowDeleteConfirm(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="bg-red-600 hover:bg-red-700"
                      onClick={() => {
                        onDelete(user.id);
                        setShowDeleteConfirm(false);
                      }}
                      data-testid={`confirm-delete-user-${user.id}`}
                    >
                      Delete User
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>
    </Card>
  );
};

const WorkLogCard = ({ log, tasks, onDelete, onDownloadPhoto, onDownloadAll }) => {
  const task = tasks.find(t => t.id === log.task_id);
  
  // Format address from geocoded data
  const formatAddress = () => {
    if (log.geocoded_address) {
      const addr = log.geocoded_address;
      // Build a formatted address string
      const parts = [];
      
      if (addr.road) parts.push(addr.road);
      if (addr.suburb) parts.push(addr.suburb);
      if (addr.city) parts.push(addr.city);
      if (addr.state) parts.push(addr.state);
      if (addr.postcode) parts.push(`PIN: ${addr.postcode}`);
      
      return parts.length > 0 ? parts.join(', ') : addr.display_name || 'Address not available';
    }
    return 'Address not available';
  };

  return (
    <Card className="p-6" data-testid={`worklog-card-${log.id}`}>
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <h3 className="font-bold text-slate-900">{log.user_name}</h3>
          <p className="text-sm text-slate-600">Task: {task?.title || 'Unknown'}</p>
        </div>
        <div className="flex gap-2 items-start">
          <span className="text-sm text-slate-500">{new Date(log.timestamp).toLocaleString()}</span>
          <Button
            onClick={() => onDelete(log.id)}
            variant="ghost"
            size="sm"
            className="text-red-600 hover:text-red-700 h-8 w-8 p-0"
            data-testid={`delete-log-btn-${log.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      {log.notes && <p className="text-slate-700 mb-2">{log.notes}</p>}
      {(log.gps_latitude && log.gps_longitude) && (
        <div className="mb-2 space-y-1">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <MapPin className="w-4 h-4" />
            <div className="flex-1">
              <div className="font-medium text-slate-700">{formatAddress()}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                Coordinates: {log.gps_latitude.toFixed(6)}, {log.gps_longitude.toFixed(6)}
              </div>
            </div>
            <a
              href={`https://www.google.com/maps?q=${log.gps_latitude},${log.gps_longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-teal-600 hover:text-teal-700"
              title="View on Google Maps"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      )}
      {log.photos && log.photos.length > 0 && (
        <div>
          <div className="flex justify-between items-center mb-2">
            <p className="text-sm font-medium text-slate-700">{log.photos.length} Photo(s)</p>
            <Button
              onClick={() => onDownloadAll(log.photos, task?.title || 'work')}
              variant="outline"
              size="sm"
              className="gap-1"
            >
              <Download className="w-3 h-3" />
              Download All
            </Button>
          </div>
          <div className="flex gap-2 flex-wrap">
            {log.photos.map((photo, i) => (
              <div key={i} className="relative group">
                <img src={photo} alt="Work" className="w-20 h-20 object-cover rounded" />
                <button
                  onClick={() => onDownloadPhoto(photo, i)}
                  className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded"
                >
                  <Download className="w-5 h-5 text-white" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};

const MessageCard = ({ message, tasks }) => {
  const task = tasks.find(t => t.id === message.task_id);
  return (
    <Card className="p-6" data-testid={`message-card-${message.id}`}>
      <div className="flex justify-between items-start mb-2">
        <div>
          <h3 className="font-bold text-slate-900">{message.from_user_name}</h3>
          <p className="text-sm text-slate-600">Task: {task?.title || 'Unknown'}</p>
        </div>
        <span className="text-sm text-slate-500">{new Date(message.timestamp).toLocaleString()}</span>
      </div>
      <p className="text-slate-700">{message.message}</p>
    </Card>
  );
};

const CreateTaskForm = ({ users, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assigned_to: '',
    customer_name: '',
    customer_details: '',
    deadline_hours: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit}>
      <DialogHeader>
        <DialogTitle>Create New Task</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 mt-4">
        <div>
          <label className="text-sm font-medium mb-2 block">Title</label>
          <Input 
            data-testid="task-title-input"
            value={formData.title} 
            onChange={(e) => setFormData({...formData, title: e.target.value})} 
            required 
          />
        </div>
        <div>
          <label className="text-sm font-medium mb-2 block">Description</label>
          <Textarea 
            data-testid="task-description-input"
            value={formData.description} 
            onChange={(e) => setFormData({...formData, description: e.target.value})} 
            required 
          />
        </div>
        <div>
          <label className="text-sm font-medium mb-2 block">Assign To</label>
          <Select value={formData.assigned_to} onValueChange={(val) => setFormData({...formData, assigned_to: val})} required>
            <SelectTrigger data-testid="task-assign-select">
              <SelectValue placeholder="Select staff" />
            </SelectTrigger>
            <SelectContent>
              {users.map(u => (
                <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-sm font-medium mb-2 block">Customer Name (Optional)</label>
          <Input 
            data-testid="task-customer-name-input"
            value={formData.customer_name} 
            onChange={(e) => setFormData({...formData, customer_name: e.target.value})} 
          />
        </div>
        <div>
          <label className="text-sm font-medium mb-2 block">Customer Details (Optional)</label>
          <Textarea 
            data-testid="task-customer-details-input"
            value={formData.customer_details} 
            onChange={(e) => setFormData({...formData, customer_details: e.target.value})} 
          />
        </div>
        <div>
          <label className="text-sm font-medium mb-2 block">Deadline (Hours) *</label>
          <Input 
            type="number"
            placeholder="Hours from now (required)"
            data-testid="task-deadline-input"
            value={formData.deadline_hours} 
            onChange={(e) => setFormData({...formData, deadline_hours: e.target.value})} 
            min="1"
            required
          />
          <p className="text-xs text-slate-500 mt-1">Required. Enter deadline in hours (e.g., 24 for 1 day, 168 for 1 week)</p>
        </div>
        <div className="flex gap-2 justify-end">
          <Button type="button" variant="outline" onClick={onCancel} data-testid="cancel-task-btn">Cancel</Button>
          <Button type="submit" className="bg-teal-600 hover:bg-teal-700" data-testid="submit-task-btn">Create Task</Button>
        </div>
      </div>
    </form>
  );
};

const TaskDetail = ({ task, workLogs, onDownloadPhoto, onDownloadAll }) => {
  return (
    <div>
      <DialogHeader>
        <DialogTitle>{task.title}</DialogTitle>
      </DialogHeader>
      <div className="mt-4 space-y-4">
        <div>
          <h4 className="font-semibold text-slate-900 mb-1">Description</h4>
          <p className="text-slate-700">{task.description}</p>
        </div>
        {task.customer_name && (
          <div>
            <h4 className="font-semibold text-slate-900 mb-1">Customer</h4>
            <p className="text-slate-700">{task.customer_name}</p>
            {task.customer_details && <p className="text-sm text-slate-600">{task.customer_details}</p>}
          </div>
        )}
        <div>
          <h4 className="font-semibold text-slate-900 mb-2">Work Logs ({workLogs.length})</h4>
          <div className="space-y-3">
            {workLogs.map(log => (
              <WorkLogDetailCard 
                key={log.id} 
                log={log}
                onDownloadPhoto={onDownloadPhoto}
                onDownloadAll={onDownloadAll}
                taskTitle={task.title}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const WorkLogDetailCard = ({ log, onDownloadPhoto, onDownloadAll, taskTitle }) => {
  const [address, setAddress] = useState('Loading...');

  useEffect(() => {
    if (log.gps_latitude && log.gps_longitude) {
      fetchAddress(log.gps_latitude, log.gps_longitude);
    }
  }, [log.gps_latitude, log.gps_longitude]);

  const fetchAddress = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
      );
      const data = await response.json();
      if (data.display_name) {
        setAddress(data.display_name);
      } else {
        setAddress('Address not found');
      }
    } catch (error) {
      setAddress('Unable to fetch');
    }
  };

  return (
    <div className="p-4 bg-slate-50 rounded-lg">
      <p className="text-sm text-slate-600 mb-2">{new Date(log.timestamp).toLocaleString()}</p>
      {log.notes && <p className="text-slate-700 mb-2">{log.notes}</p>}
      {(log.gps_latitude && log.gps_longitude) && (
        <div className="mb-2 space-y-1">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <MapPin className="w-4 h-4" />
            <span>{log.gps_latitude.toFixed(6)}, {log.gps_longitude.toFixed(6)}</span>
            <a
              href={`https://www.google.com/maps?q=${log.gps_latitude},${log.gps_longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-teal-600 hover:text-teal-700"
            >
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <p className="text-xs text-slate-500 ml-6">{address}</p>
        </div>
      )}
      {log.photos && log.photos.length > 0 && (
        <div>
          <div className="flex justify-between items-center mb-2">
            <p className="text-sm font-medium">{log.photos.length} Photo(s)</p>
            <Button
              onClick={() => onDownloadAll(log.photos, taskTitle)}
              variant="outline"
              size="sm"
              className="gap-1 h-7"
            >
              <Download className="w-3 h-3" />
              Download All
            </Button>
          </div>
          <div className="flex gap-2 flex-wrap">
            {log.photos.map((photo, i) => (
              <div key={i} className="relative group">
                <img src={photo} alt="Work" className="w-24 h-24 object-cover rounded cursor-pointer" onClick={() => window.open(photo, '_blank')} />
                <button
                  onClick={() => onDownloadPhoto(photo, i)}
                  className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded"
                >
                  <Download className="w-5 h-5 text-white" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
